<script lang="js" setup>
import { useFutureStore } from '~/store/future'
import { div, fix, percent } from '~/utils/math'
import xMod from '~/components/common/xMod.vue'
import { useUserStore } from '~/store/user'

const { t } = useI18n()
const localePath = useLocalePath()
const user = useUserStore()
const future = useFutureStore()
const assetExpand = ref(true)
const curTab = ref('future')

const tabs = computed(() => {
  const list = [
    {
      title: t('资产'),
      key: 'future',
    },
  ]

  if (future.isBonusOpen) {
    list.push({
      title: t('体验金'),
      key: 'bonus',
    })
  }
  return list
})

const accountName = computed(() => tabs.value.find(i => curTab.value === i.key).key)
const account = computed(() => future.getBalanceByCode(future.baseM, accountName.value))

// 已用
const used = computed(() => {
  const asset = account.value
  return add(asset.initMargin, asset.frozenForTrade)
})

// sum (所有全仓仓位的维持保证金) /（账户权益-逐仓冻结-逐仓未实现盈亏-逐仓仓位保证金）:  逐仓未实现盈亏 需要大于0  小于 0 不减
const mm = computed(() => {
  const crossPosis = future.positionsByType(future.isGrants ? 10 : undefined).filter(i => !i.isolated)
  const totalCrossInterest = crossPosis.reduce((sofar, cur) => {
    sofar = add(sofar, cur.interest)
    return sofar
  }, 0)

  // 分母
  const b = future.getEquity(future.isGrants ? 'bonus' : future.accountName).cross

  let mmr = 0
  if (b.gt(0)) {
    mmr = div(totalCrossInterest, b).toFixed()
  }

  return {
    a: totalCrossInterest,
    r: mmr,
  }
})

// 全仓+逐仓未实现盈亏之和
const totalUnpl = computed(() => {
  return future.positions.filter(i => i.accountName === accountName.value).reduce((sofar, cur) => {
    const extra = future.positionsExtra[cur.id] ?? {}
    sofar = add(sofar, extra.upnl ?? 0)
    return sofar
  }, 0)
})
</script>

<template>
  <div class="px-3">
    <div class="h-px -mx-3 border-t border-pageTertiary" />
    <div class="flex items-center py-3 text-xs font-medium">
      <CommonXTipsSimple>
        <template #content>
          <ul>
            <li>{{ $t('合约交易手续费: 您在交易合约时由交易所收取手续费。') }}</li>
            <li>{{ $t('手续费公式: 手续费=已成交委托价值x手续费费率') }}</li>
            <li>{{ $t('特别说明: 手续费以保证金货币收取。 撤销的订单无需手续费。') }}</li>
          </ul>
        </template>
        <div class="box-border h-4 border-b border-dashed border-border">
          {{ $t('手续费率') }}
        </div>
      </CommonXTipsSimple>
      <div class="ps-2 ms-2 me-4 border-l border-border">
        {{ $t('挂单') }}: {{ percent(future.curInfo.makerFeeRate, 3, false) }}
      </div>
      <div>{{ $t('吃单') }}: {{ percent(future.curInfo.takerFeeRate, 3, false) }}</div>
    </div>
    <div class="h-px -mx-3 border-t-[2px] border-pageTertiary" />
    <div class="pt-2 pb-4 text-sm">
      <xMod :tab="curTab" :tabs="tabs" header-class="!px-0 !h-8 border-none" @tab-change="(v) => curTab = v" />
      <div class="h-px -mx-3 border-t border-pageTertiary" />
      <ClientOnly>
        <template v-if="user.isLogin">
          <div class="mt-3 space-y-2 overflow-hidden text-xs transition text-textSecondary" :class="[assetExpand ? 'h-auto' : 'h-[90px]']">
            <div class="flex items-center justify-between">
              <n-tooltip trigger="hover" placement="top-start" style="max-width: 240px;">
                <div class="text-xs">
                  {{ $t('您在交易所持有的总权益账户权益=账户余额+未实现盈亏之和') }}
                </div>
                <template #trigger>
                  <div class="border-b border-dashed border-t2">
                    {{ $t('账户权益') }}
                  </div>
                </template>
              </n-tooltip>
              <div class="font-medium text-textPrimary">
                {{ fix(future.getEquity(accountName).t, 2) }}
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div>{{ $t('可用保证金') }}</div>
              <div class="font-medium text-textPrimary">
                {{ fix(account?.trade ?? 0, 2) }}
              </div>
            </div>
            <div class="flex items-center justify-between">
              <n-tooltip trigger="hover" placement="top-start" style="max-width: 240px;">
                <template #trigger>
                  <div class="border-b border-dashed border-t2">
                    {{ $t('未实现盈亏') }}
                  </div>
                </template>
                <div class="text-xs">
                  {{ $t('净盈利，不包含手续费、资金费用') }}
                </div>
              </n-tooltip>

              <div class="font-medium text-textPrimary">
                {{ fix(totalUnpl ?? 0, 2) }}
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div>{{ $t('持仓占用保证金') }}</div>
              <div class="font-medium text-textPrimary">
                {{ fix(used ?? 0, 2) }}
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div>{{ $t('委托占用保证金') }}</div>
              <div class="font-medium text-textPrimary">
                {{ fix(account.frozenForTrade ?? 0, 2) }}
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2 mt-10">
            <NuxtLink class="w-full" :to="localePath(`/assets/deposit/${future.baseM}`)">
              <CommonXButton type="default" class="w-full">
                {{ $t('充值') }}
              </CommonXButton>
            </NuxtLink>
            <NuxtLink :to="localePath(`/assets/transfer`)">
              <CommonXButton class="w-full" type="default">
                {{ $t('划转') }}
              </CommonXButton>
            </NuxtLink>
          </div>
        </template>
        <CommonOrderLogin v-else class="!pt-12" />
      </ClientOnly>
    </div>
  </div>
</template>
